import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Rating,
  TextField,
  Typography,
} from '@mui/material';
import api from '../config/api';
import SEOHead from '../components/SEOHead';
import {
  DOCUMENT,
  PAGE_CONTAINER_MAX_WIDTH,
  documentPageCanvasSx,
  documentPageContainerSx,
  documentAuthPaperSx,
  documentAuthHeadingSx,
  documentAuthFieldSx,
  documentAuthButtonSx,
  documentAuthAlertSx,
  documentEyebrowSx,
} from '../constants/documentTheme';

const formatTripDates = (startDate, endDate) => {
  const start = startDate ? new Date(startDate) : null;
  const end = endDate ? new Date(endDate) : null;
  const opts = { month: 'short', day: 'numeric', year: 'numeric' };
  if (start && !Number.isNaN(start.getTime()) && end && !Number.isNaN(end.getTime())) {
    return `${start.toLocaleDateString(undefined, opts)} – ${end.toLocaleDateString(undefined, opts)}`;
  }
  return '';
};

const TripRating = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [trip, setTrip] = useState(null);
  const [partner, setPartner] = useState(null);
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState('');
  const [existing, setExisting] = useState(false);
  const [canRate, setCanRate] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/trips/${id}/rating`);
      setTrip(response.data.trip || null);
      setPartner(response.data.partner || null);
      setCanRate(response.data.canRate !== false);
      const rating = response.data.rating;
      if (rating?.stars) {
        setStars(rating.stars);
        setComment(rating.comment || '');
        setExisting(true);
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 403) {
        setError(t('tripRating.notMember'));
      } else if (status === 400) {
        setError(err.response?.data?.error || t('tripRating.noAdvisor'));
      } else if (status === 401) {
        setError(t('tripRating.loginRequired'));
      } else {
        setError(err.response?.data?.error || t('tripRating.loadFailed'));
      }
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSubmit = async (event) => {
    if (event?.preventDefault) event.preventDefault();
    if (!stars) {
      setError(t('tripRating.starsRequired'));
      return;
    }
    try {
      setSaving(true);
      setError('');
      setSuccess(false);
      const response = await api.put(`/trips/${id}/rating`, { stars, comment });
      setExisting(true);
      setSuccess(true);
      if (response.data?.rating) {
        setStars(response.data.rating.stars);
        setComment(response.data.rating.comment || '');
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 403) {
        setError(t('tripRating.notMember'));
      } else if (err.response?.data?.code === 'TRIP_NOT_ENDED') {
        setCanRate(false);
        setError(t('tripRating.notEnded'));
      } else {
        setError(err.response?.data?.error || t('tripRating.loadFailed'));
      }
    } finally {
      setSaving(false);
    }
  };

  const advisorName = partner?.name || t('tripRating.advisorLabel');

  return (
    <Box sx={documentPageCanvasSx}>
      <SEOHead
        title={t('tripRating.title')}
        description={t('tripRating.subtitle', {
          advisor: advisorName,
          tripTitle: trip?.title || '',
        })}
      />
      <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={documentPageContainerSx}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper elevation={0} sx={{ ...documentAuthPaperSx, maxWidth: 640, mx: 'auto' }}>
            <Typography sx={documentEyebrowSx}>{t('tripRating.eyebrow')}</Typography>
            <Typography variant="h4" component="h1" sx={{ ...documentAuthHeadingSx, mb: 1 }}>
              {t('tripRating.title')}
            </Typography>
            <Typography sx={{ color: DOCUMENT.ink2, mb: 3 }}>
              {t('tripRating.subtitle', {
                advisor: advisorName,
                tripTitle: trip?.title || '',
              })}
            </Typography>

            {trip && (
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  border: `1px solid ${DOCUMENT.rule}`,
                  bgcolor: DOCUMENT.ivory,
                }}
              >
                <Typography sx={{ fontWeight: 700, color: DOCUMENT.ink, mb: 1 }}>
                  {trip.title}
                </Typography>
                <Typography sx={{ fontSize: 14, color: DOCUMENT.ink2 }}>
                  {t('tripRating.advisorLabel')}: {advisorName}
                </Typography>
                {trip.destination && (
                  <Typography sx={{ fontSize: 14, color: DOCUMENT.ink2 }}>
                    {t('tripRating.destination')}: {trip.destination}
                  </Typography>
                )}
                {formatTripDates(trip.startDate, trip.endDate) && (
                  <Typography sx={{ fontSize: 14, color: DOCUMENT.ink2 }}>
                    {t('tripRating.dates')}: {formatTripDates(trip.startDate, trip.endDate)}
                  </Typography>
                )}
              </Box>
            )}

            {!canRate && trip && (
              <Alert severity="info" sx={documentAuthAlertSx} data-testid="trip-rating-not-ended">
                {t('tripRating.notEnded')}
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={documentAuthAlertSx}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={documentAuthAlertSx}>
                {t('tripRating.success')}
              </Alert>
            )}

            {trip && canRate && (
              <Box component="form" onSubmit={handleSubmit}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>
                  {t('tripRating.starsLabel')}
                </Typography>
                <Rating
                  name="trip-rating-stars"
                  value={stars}
                  onChange={(_, value) => setStars(value || 0)}
                  size="large"
                  sx={{ mb: 3, color: DOCUMENT.oxblood }}
                />
                <TextField
                  label={t('tripRating.commentsLabel')}
                  placeholder={t('tripRating.commentsPlaceholder')}
                  value={comment}
                  onChange={(event) => setComment(event.target.value)}
                  multiline
                  minRows={4}
                  fullWidth
                  inputProps={{ maxLength: 2000 }}
                  sx={{ ...documentAuthFieldSx, mb: 3 }}
                />
                <Button
                  type="button"
                  variant="contained"
                  disabled={saving}
                  onClick={handleSubmit}
                  data-testid="submit-trip-rating"
                  sx={documentAuthButtonSx}
                >
                  {saving
                    ? t('tripRating.submitting')
                    : existing
                      ? t('tripRating.update')
                      : t('tripRating.submit')}
                </Button>
              </Box>
            )}
            {trip && (
              <Button
                component={RouterLink}
                to={`/trips/${id}`}
                sx={{ display: 'block', mt: 2, color: DOCUMENT.oxblood }}
              >
                {t('tripRating.openTrip')}
              </Button>
            )}
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default TripRating;
